import type { InputHTMLAttributes, ReactNode, Ref, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import type { ItemType, Visibility } from '../api/types';
import { initials, messageOf, typeLabel, visibilityLabel } from '../lib/format';
import { LOCALE_NAMES, t, useLocale, writeLocale, type Locale } from '../i18n';
import { IconAlert, TypeIcon, VISIBILITY_ICONS } from './icons';

/* ---------- form fields ---------- */

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, hint, error, children }: FieldProps) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <p className="field__error" id={`${htmlFor}-error`}>
          {error}
        </p>
      ) : hint ? (
        <p className="field__hint" id={`${htmlFor}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function TextInput({
  invalid,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; ref?: Ref<HTMLInputElement> }) {
  return <input className="input" aria-invalid={invalid || undefined} {...rest} />;
}

export function TextArea({
  invalid,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean; ref?: Ref<HTMLTextAreaElement> }) {
  return <textarea className="textarea" aria-invalid={invalid || undefined} {...rest} />;
}

export function Select({
  invalid,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean; ref?: Ref<HTMLSelectElement> }) {
  return <select className="select" aria-invalid={invalid || undefined} {...rest} />;
}

/* ---------- identity & taxonomy ---------- */

export function Avatar({
  name,
  src,
  size = 'md',
}: {
  name: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const className = size === 'sm' ? 'avatar avatar--sm' : size === 'lg' ? 'avatar avatar--lg' : 'avatar';
  return (
    <span className={className} aria-hidden="true">
      {src ? <img src={src} alt="" loading="lazy" /> : initials(name)}
    </span>
  );
}

export function TypeBadge({ type }: { type: ItemType }) {
  return (
    <span className="badge badge--signal">
      <TypeIcon type={type} size={13} />
      {typeLabel(type)}
    </span>
  );
}

export function VisibilityBadge({ visibility }: { visibility: Visibility }) {
  const Icon = VISIBILITY_ICONS[visibility] ?? VISIBILITY_ICONS.public;
  const tone = visibility === 'private' ? '' : visibility === 'followers' ? 'badge--ember' : 'badge--aurora';
  return (
    <span className={`badge ${tone}`.trim()}>
      <Icon size={13} />
      {visibilityLabel(visibility)}
    </span>
  );
}

export function SourceLabel({ name, domain }: { name?: string | null; domain?: string | null }) {
  if (!name && !domain) return null;
  return (
    <span className="badge">
      <span className="dot" aria-hidden="true" />
      {name ?? domain}
    </span>
  );
}

export function UserLink({
  username,
  displayName,
  avatarUrl,
  size = 'sm',
}: {
  username: string;
  displayName: string;
  avatarUrl?: string | null;
  size?: 'sm' | 'md';
}) {
  return (
    <Link to={`/u/${username}`} className="row row--tight" style={{ color: 'inherit' }}>
      <Avatar name={displayName} src={avatarUrl} size={size} />
      <span className="strong clamp-1">{displayName}</span>
      <span className="muted tiny nowrap">@{username}</span>
    </Link>
  );
}

/* ---------- screen states ---------- */

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack" role="status" aria-label={t('ui.loading')}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="stack-sm">
          <div className="skeleton skeleton--title" />
          <div className="skeleton skeleton--text" style={{ width: `${70 - index * 6}%` }} />
        </div>
      ))}
      <span className="sr-only">{t('ui.loadingShort')}</span>
    </div>
  );
}

export function CardSkeletons({ count = 6, variant = 'shelf' }: { count?: number; variant?: 'shelf' | 'playlist' }) {
  return (
    <div className={variant === 'playlist' ? 'playlist-grid' : 'grid'} aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="skeleton-card">
          <div className={`skeleton skeleton--cover${variant === 'playlist' ? ' skeleton--playlist' : ''}`} />
          <div className="stack-sm">
            <div className="skeleton skeleton--title" />
            <div className="skeleton skeleton--text" />
            <div className="skeleton skeleton--text" style={{ width: '60%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      {icon ? <div className="state__icon">{icon}</div> : null}
      <p className="state__title">{title}</p>
      {body ? <p className="state__body">{body}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = messageOf(error, t('ui.loadError'));
  return (
    <div className="state" role="alert">
      <div className="state__icon" style={{ color: 'var(--rose)' }}>
        <IconAlert />
      </div>
      <p className="state__title">{t('ui.errorTitle')}</p>
      <p className="state__body">{message}</p>
      {onRetry ? (
        <button type="button" className="btn btn--outline" onClick={onRetry}>
          {t('ui.retry')}
        </button>
      ) : null}
    </div>
  );
}

/* ---------- small layout bits ---------- */

export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="section__head">
      <h2>{children}</h2>
      {aside}
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  name,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
  name?: string;
}) {
  return (
    <div className="field">
      <span className="field__label" id={name ? `${name}-label` : undefined}>
        {label}
      </span>
      <div className="segmented" role="group" aria-labelledby={name ? `${name}-label` : label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="segmented__btn"
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function LanguageToggle({ className = 'btn btn--outline btn--sm' }: { className?: string }) {
  const locale = useLocale();
  const other: Locale = locale === 'ru' ? 'en' : 'ru';
  return (
    <button type="button" className={className} onClick={() => writeLocale(other)}>
      {LOCALE_NAMES[other]}
    </button>
  );
}

